/**
 * AuthPage.js — Login screen
 *
 * Shown at /login. Redirects authenticated users to / via PublicRoute in App.js.
 *
 * Auth flow:
 *   1. User submits email + password.
 *   2. AuthContext.login() → POST /auth/login (mode: 'session').
 *   3. On success, Directus sets an httpOnly cookie and AuthContext fetches
 *      the user profile, stores it in localStorage, then updates currentUser.
 *   4. React Router re-evaluates: PublicRoute now sees currentUser and
 *      redirects to / automatically — no manual navigate() needed here.
 *
 * Error handling: Directus wraps all errors as { errors: [{ message }] }.
 * The first error message is shown inline below the form fields.
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function AuthPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // No navigate() here — PublicRoute detects the new currentUser and redirects
    } catch (err) {
      setError(err?.errors?.[0]?.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8fafc',
      padding: '16px',
    }}>
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '0.375rem',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        padding: '32px',
        width: '100%',
        maxWidth: '360px',
      }}>
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px' }}>
            Haziri App
          </div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            Innomalous Technologies
          </div>
        </div>

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter email"
              autoFocus
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
              required
            />
          </div>

          {error && (
            <div style={{
              padding: '8px 12px',
              background: '#fff5f5',
              border: '1px solid #fecaca',
              borderRadius: '0.375rem',
              color: '#b30000',
              fontSize: '12px',
              marginBottom: '14px',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '9px' }}
            disabled={loading}
          >
            {loading ? 'Logging in…' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}
